import {SUBSCRIPTION_TYPES_ALLOWED_ZERO_BUDGET} from '@/views/campaigns/brief/constants';
import type {BriefAiWizardFormErrors} from './BriefAiWizardProps';
import {BriefAiWizardFormProps} from './BriefAiWizardProps';
import Step from './step/Step';
import BRIEF_WIZARD_TEST_IDS from './testIds';
import {stringToNumber} from './validators';

const TRANSLATION_PREFIX = 'views.campaigns.components.wizard';

// step validator functions => true if step is valid, false if not
const STEP_VALIDATORS = {
  budgetAndNetwork: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) => {
    const {budget, customBudget, network, subscriptionType} = formData;
    let isBudgetValid = !!budget || !!customBudget;

    if (
      subscriptionType &&
      SUBSCRIPTION_TYPES_ALLOWED_ZERO_BUDGET.includes(subscriptionType)
    ) {
      isBudgetValid =
        isBudgetValid || (!!customBudget && stringToNumber(customBudget) === 0);
    }
    return isBudgetValid && !!network && !formErrors.budget;
  },
  specialties: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) => {
    return true;
  },
  demographics: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) => {
    return true;
  },
  deliverables: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) => {
    const {requiredDeliverables} = formData;
    return !!requiredDeliverables.length && !formErrors.requiredDeliverables;
  },
  influencerPackage: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) => {
    const {creatorType} = formData;
    return !!creatorType;
  },
  product: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) => {
    const {
      product,
      sendProductToCreators,
      sendProductToCreatorsMethod,
      sendProductToCreatorsCashValue,
    } = formData;

    return (
      !!product &&
      (!sendProductToCreators ||
        (sendProductToCreators &&
          !!sendProductToCreatorsMethod &&
          !!sendProductToCreatorsCashValue))
    );
  },
};

type ValidateStepsProps = {
  validators: ((
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) => boolean)[];
  formData: BriefAiWizardFormProps;
  formErrors: BriefAiWizardFormErrors;
};

const validateSteps = ({
  validators,
  formData,
  formErrors,
}: ValidateStepsProps): boolean => {
  return validators.every(
    (
      validator: (
        formData: BriefAiWizardFormProps,
        formErrors: BriefAiWizardFormErrors
      ) => boolean
    ) => validator(formData, formErrors)
  );
};

const budgetAndNetworkStep = {
  testID: BRIEF_WIZARD_TEST_IDS.STEP__BUDGET_AND_NETWORK,
  id: 'budgetAndNetwork',
  labelLocaleKey: `${TRANSLATION_PREFIX}.steps.budget-and-network.label`,
  Component: Step.BudgetAndNetwork,
  errorCheck: (formData: BriefAiWizardFormProps) => false,
  canContinueToStep: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) =>
    validateSteps({
      validators: [STEP_VALIDATORS.budgetAndNetwork],
      formData,
      formErrors,
    }),
};

const demographicsStep = {
  testID: BRIEF_WIZARD_TEST_IDS.STEP__DEMOGRAPHICS,
  id: 'demographics',
  labelLocaleKey: 'Select age range, gender and location',
  Component: Step.Demographics,
  errorCheck: (formData: BriefAiWizardFormProps) => false,
  canContinueToStep: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) =>
    validateSteps({
      validators: [
        STEP_VALIDATORS.budgetAndNetwork,
        STEP_VALIDATORS.demographics,
      ],
      formData,
      formErrors,
    }),
};

const specialtiesStep = {
  testID: BRIEF_WIZARD_TEST_IDS.STEP__SPECIALTIES,
  id: 'specialties',
  labelLocaleKey: 'Select specialties',
  Component: Step.Specialties,
  errorCheck: (formData: BriefAiWizardFormProps) => false,
  canContinueToStep: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) =>
    validateSteps({
      validators: [
        STEP_VALIDATORS.budgetAndNetwork,
        STEP_VALIDATORS.demographics,
        STEP_VALIDATORS.specialties,
      ],
      formData,
      formErrors,
    }),
};

const deliverablesStep = {
  testID: BRIEF_WIZARD_TEST_IDS.STEP__DELIVERABLES,
  id: 'deliverables',
  labelLocaleKey: `${TRANSLATION_PREFIX}.steps.deliverables.label`,
  Component: Step.Deliverables,
  errorCheck: (formData: BriefAiWizardFormProps) => false,
  canContinueToStep: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) =>
    validateSteps({
      validators: [
        STEP_VALIDATORS.budgetAndNetwork,
        STEP_VALIDATORS.demographics,
        STEP_VALIDATORS.specialties,
        STEP_VALIDATORS.deliverables,
      ],
      formData,
      formErrors,
    }),
};

const influencerPackageStep = {
  testID: BRIEF_WIZARD_TEST_IDS.STEP__INFLUENCER_PACKAGE,
  id: 'influencerPackage',
  labelLocaleKey: `${TRANSLATION_PREFIX}.steps.influencer-package.label`,
  Component: Step.InfluencerPackage,
  errorCheck: (formData: BriefAiWizardFormProps) => false,
  canContinueToStep: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) =>
    validateSteps({
      validators: [
        STEP_VALIDATORS.budgetAndNetwork,
        STEP_VALIDATORS.demographics,
        STEP_VALIDATORS.specialties,
        STEP_VALIDATORS.deliverables,
        STEP_VALIDATORS.influencerPackage,
      ],
      formData,
      formErrors,
    }),
};

const productStep = {
  testID: BRIEF_WIZARD_TEST_IDS.STEP__PRODUCT,
  id: 'product',
  labelLocaleKey: `${TRANSLATION_PREFIX}.steps.product.label`,
  Component: Step.Product,
  errorCheck: (formData: BriefAiWizardFormProps) => false,
  canContinueToStep: (
    formData: BriefAiWizardFormProps,
    formErrors: BriefAiWizardFormErrors
  ) =>
    validateSteps({
      validators: [
        STEP_VALIDATORS.budgetAndNetwork,
        STEP_VALIDATORS.demographics,
        STEP_VALIDATORS.specialties,
        STEP_VALIDATORS.deliverables,
        STEP_VALIDATORS.influencerPackage,
        STEP_VALIDATORS.product,
      ],
      formData,
      formErrors,
    }),
};
export const BRAND_AWARENESS_CAMPAIGN_STEPS = [
  budgetAndNetworkStep,
  demographicsStep,
  specialtiesStep,
  deliverablesStep,
  influencerPackageStep,
  productStep,
];

export const CONTENT_CREATION_CAMPAIGN_STEPS = [
  budgetAndNetworkStep,
  demographicsStep,
  specialtiesStep,
  deliverablesStep,
  {
    ...productStep,
    canContinueToStep: (
      formData: BriefAiWizardFormProps,
      formErrors: BriefAiWizardFormErrors
    ) =>
      validateSteps({
        validators: [
          STEP_VALIDATORS.budgetAndNetwork,
          STEP_VALIDATORS.demographics,
          STEP_VALIDATORS.specialties,
          STEP_VALIDATORS.deliverables,
          STEP_VALIDATORS.product,
        ],
        formData,
        formErrors,
      }),
  },
];
